import { createContext, useContext, useState, useCallback } from "react";

const VideoPlayerContext = createContext(null);

export function VideoPlayerProvider({ children }) {
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isMinimized, setIsMinimized] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [failedIds, setFailedIds] = useState(new Set());

  const playVideo = useCallback((video, videos = null) => {
    setCurrentVideo(video);
    setIsVisible(true);
    setIsMinimized(false);
    if (Array.isArray(videos)) setPlaylist(videos);
  }, []);

  const closeVideo = useCallback(() => {
    setIsVisible(false);
    setCurrentVideo(null);
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  // Auto-skip to next highlight when a video fails to embed
  const skipToNext = useCallback(() => {
    setCurrentVideo((prev) => {
      if (!prev) return null;
      setFailedIds((f) => {
        const updated = new Set(f);
        updated.add(prev.youtubeId);
        return updated;
      });
      const pool = playlist.length ? playlist : [prev];
      const nextIdx = pool.findIndex((v) => v.youtubeId === prev.youtubeId) + 1;
      for (let i = 0; i < pool.length; i++) {
        const candidate = pool[(nextIdx + i) % pool.length];
        // Exclude the current (just-failed) video and any previously-failed ones
        if (candidate.youtubeId !== prev.youtubeId && !failedIds.has(candidate.youtubeId)) {
          return candidate;
        }
      }
      return prev;
    });
  }, [playlist, failedIds]);

  return (
    <VideoPlayerContext.Provider value={{
      currentVideo, isMinimized, isVisible, playlist,
      playVideo, closeVideo, toggleMinimize, setIsMinimized, skipToNext,
    }}>
      {children}
    </VideoPlayerContext.Provider>
  );
}

export function useVideoPlayer() {
  const ctx = useContext(VideoPlayerContext);
  if (!ctx) throw new Error("useVideoPlayer must be used within VideoPlayerProvider");
  return ctx;
}
